import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Hook for the Filter Labels browse page (Phase 16, plan 16-04).
 *
 * Mirrors useThemeStrings.js with three deltas:
 *  - Endpoint base: /api/transly/filter-labels/ (not /theme-strings/)
 *  - Drops the resource-type and prefix-filter state (theme-strings concepts
 *    that don't apply to FILTER resources — single resource type, single
 *    translatable key per resource)
 *  - PATCH path uses `filterLabelId` naming convention
 *
 * Server-side filters (policy, status, q) — backend serves a small filter-label
 * inventory (~20-50 rows) but pagination + filtering still flow through the
 * same SQL that powers the per-locale status pivot.
 */
export default function useFilterLabels() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side)
  const [policy, setPolicy] = useState('all');     // 'translate' | 'skip' | 'auto-skipped' | 'all'
  const [status, setStatus] = useState('all');     // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches Phase 14)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (policy && policy !== 'all') params.policy = policy;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/filter-labels/', { params }),
        axios.get('/api/transly/filter-labels/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load filter labels');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, policy, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages)
  useEffect(() => {
    setPage(1);
  }, [policy, status, debouncedQ]);

  const updatePolicy = useCallback(async (filterLabelId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === filterLabelId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/filter-labels/${filterLabelId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch this row's truth.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/filter-labels/sync/');
  }, []);

  return {
    // Data
    rows,
    total,
    loading,
    error,
    // Filters
    policy,
    setPolicy,
    status,
    setStatus,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
  };
}
